import { object as yupObject, string as yupString, number as yupNumber, mixed as yupMixed } from "yup";

const extractVal = (v) => {
  if (!v) return undefined;
  if (typeof v === "string" || typeof v === "number") return v;
  if (typeof v === "object") {
    if (v.value !== undefined && (typeof v.value === "string" || typeof v.value === "number")) return v.value;
    if (v.value !== undefined && typeof v.value === "object") return extractVal(v.value);
  }
  return undefined;
};

const numOpcional = yupNumber()
  .nullable()
  .min(0, "Valor deve ser maior ou igual a zero")
  .transform((value, originalValue) => {
    if (originalValue === "" || originalValue === null || originalValue === undefined) return null;
    return isNaN(Number(originalValue)) ? null : Number(originalValue);
  });

export const rotaFreteSchema = yupObject().shape({
  CNPJ_Emissor: yupMixed().test("required", "Transportadora é obrigatória", (v) => !!extractVal(v)),
  CNPJ_Remetente: yupMixed().nullable(),
  CNPJ_Destinatario: yupMixed().test("required", "Local de entrega é obrigatório", (v) => !!extractVal(v)),
  ID_Tipo_Veiculo: yupMixed().test("required", "Veículo é obrigatório", (v) => !!extractVal(v)),

  Chave_Regiao_Origem: yupMixed().test("required", "UF de origem é obrigatória", (v) => !!extractVal(v)),
  Chave_Local_Origem: yupMixed().test("required", "Município de origem é obrigatório", (v) => !!extractVal(v)),
  Chave_Regiao_Destino: yupMixed().test("required", "UF de destino é obrigatória", (v) => !!extractVal(v)),
  Chave_Local_Destino: yupMixed().test("required", "Município de destino é obrigatório", (v) => !!extractVal(v)),

  Data_Inicio_Validade: yupString().required("Data inicial é obrigatória"),
  Data_Termino_Validade: yupString().nullable().test(
    "dataFinalMaior",
    "Data final deve ser maior ou igual à data inicial",
    function (value) {
      const { Data_Inicio_Validade } = this.parent;
      if (!value) return true;
      const inicio = new Date(Data_Inicio_Validade.split("/").reverse().join("-"));
      const fim = new Date(value.split("/").reverse().join("-"));
      return fim >= inicio;
    }
  ),

  Frete_Fixo: numOpcional,
  Frete_Peso: numOpcional,
  Frete_Percentual: numOpcional,
  Frete_Cubagem: numOpcional,
  Frete_Perc_Servico: numOpcional,
  Fator_Cubagem: numOpcional,

  Frete_Minimo: numOpcional,
  Peso_Minimo: numOpcional,
  Peso_Maximo: numOpcional.test("faixaPeso", "O peso mínimo não pode ser maior que o peso máximo", function (value) {
    const { Peso_Minimo } = this.parent;
    if (value == null || Peso_Minimo == null) return true;
    return value >= Peso_Minimo;
  }),
  Valor_NF_Minimo: numOpcional,
  Valor_NF_Maximo: numOpcional.test("faixaNF", "O valor mínimo da NF não pode ser maior que o valor máximo da NF", function (value) {
    const { Valor_NF_Minimo } = this.parent;
    if (value == null || Valor_NF_Minimo == null) return true;
    return value >= Valor_NF_Minimo;
  }),
  Valor_Adicional_Fator: numOpcional,
});

export const validarAoMenosUmComponente = (values) => {
  const componentes = [
    values.Frete_Fixo,
    values.Frete_Peso,
    values.Frete_Percentual,
    values.Frete_Cubagem,
    values.Frete_Perc_Servico,
    values.Fator_Cubagem,
  ];
  const aoMenosUm = componentes.some((v) => v != null && Number(v) > 0);
  if (!aoMenosUm) {
    return "Informe ao menos um componente de frete válido para criar a rota.";
  }
  return null;
};

export const derivarTipoCalculo = (values) => {
  const f = (v) => Number(v) > 0;
  const fixo = f(values.Frete_Fixo);
  const peso = f(values.Frete_Peso);
  const percentual = f(values.Frete_Percentual);
  const cubagem = f(values.Frete_Cubagem);
  const servico = f(values.Frete_Perc_Servico);
  const fator = f(values.Fator_Cubagem);
  const count = [fixo, peso, percentual, cubagem, servico].filter(Boolean).length;

  if (fixo && !peso && !percentual && !cubagem && !servico) return "Fixo";
  if (peso && !fixo && !percentual && !cubagem && !servico) return "Por Peso";
  if (percentual && !fixo && !peso && !cubagem && !servico) return "% NF";
  if (cubagem && !fixo && !peso && !percentual && !servico) return "Cubagem";
  if (servico && !fixo && !peso && !percentual && !cubagem) return "% Serviço";
  if (peso && fator) return "Por Peso / Cubagem";
  if (cubagem && fator) return "Cubagem";
  if (fixo && peso) return "Fixo + Por Peso";
  if (fixo && percentual) return "Fixo + % NF";
  if (fixo && cubagem) return "Fixo + Cubagem";
  if (count >= 2) return "Composto";
  return "—";
};

export const BLOCOS_FORM = [
  { id: 1, titulo: "Identificação" },
  { id: 2, titulo: "Rota" },
  { id: 3, titulo: "Vigência da Regra" },
  { id: 4, titulo: "Componentes do Frete" },
  { id: 5, titulo: "Limites e Mínimos" },
];

export const TEXTOS_APOIO_COMPONENTES = {
  Frete_Fixo: "Valor fixo independente do peso/NF",
  Frete_Peso: "Multiplicado pelo peso total (kg)",
  Frete_Percentual: "% sobre o valor das NF's",
  Frete_Cubagem: "Multiplicado pela cubagem total",
  Frete_Perc_Servico: "% adicional sobre frete calculado",
  Fator_Cubagem: "Fator utilizado no cálculo da cubagem",
};